import { CenteredDialog } from '../ui/centered-dialog';
// apps/web/src/components/qualidade/correcao-apontamento-form.tsx — Fase 02, Plano 10 (OPS-07, D-15).
import { useEffect, useState, type ReactElement } from 'react';
import { apiMutate } from '../../lib/csrf-fetch';

export interface ApontamentoResumo {
  readonly id: string;
  readonly osId: string;
  readonly quantidadeBoa: number;
  readonly quantidadeNaoConforme: number;
  readonly motivoNc?: string | null;
  readonly consumoReal?: number;
  readonly insumoLoteId?: string | null;
  readonly createdAt: string;
}

interface CorrecaoApontamentoFormProps {
  readonly apontamentoOriginal: ApontamentoResumo;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCorrigido: () => void;
  /** Estorna a execução para cancelar uma OP, sem apagar o apontamento original. */
  readonly modo?: 'CORRECAO' | 'ESTORNO_INTEGRAL';
}

export function CorrecaoApontamentoForm({
  apontamentoOriginal,
  open,
  onClose,
  onCorrigido,
  modo = 'CORRECAO',
}: CorrecaoApontamentoFormProps): ReactElement | null {
  const [motivo, setMotivo] = useState<string>('');
  const [quantidadeBoa, setQuantidadeBoa] = useState<string>(String(apontamentoOriginal.quantidadeBoa));
  const [quantidadeNaoConforme, setQuantidadeNaoConforme] = useState<string>(
    String(apontamentoOriginal.quantidadeNaoConforme),
  );
  const [motivoNc, setMotivoNc] = useState<string>(apontamentoOriginal.motivoNc ?? '');
  const [consumoReal, setConsumoReal] = useState<string>(String(apontamentoOriginal.consumoReal ?? 0));
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const integral = modo === 'ESTORNO_INTEGRAL';
    setMotivo('');
    setQuantidadeBoa(integral ? '0' : String(apontamentoOriginal.quantidadeBoa));
    setQuantidadeNaoConforme(integral ? '0' : String(apontamentoOriginal.quantidadeNaoConforme));
    setMotivoNc(integral ? '' : (apontamentoOriginal.motivoNc ?? ''));
    setConsumoReal(integral ? '0' : String(apontamentoOriginal.consumoReal ?? 0));
    setError(null);
  }, [apontamentoOriginal, modo, open]);

  if (!open) return null;

  const qtdNcNum = parseFloat(quantidadeNaoConforme) || 0;
  const isFormValid =
    motivo.trim().length > 0 &&
    Boolean(quantidadeBoa) &&
    (qtdNcNum === 0 || motivoNc.trim().length > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setError(null);
    try {
      const res = await apiMutate(`/api/v1/apontamentos/${encodeURIComponent(apontamentoOriginal.id)}/corrigir`, {
        method: 'POST',
        body: {
          motivo: motivo.trim(),
          quantidadeBoa: parseFloat(quantidadeBoa),
          quantidadeNaoConforme: qtdNcNum,
          motivoNc: qtdNcNum > 0 ? motivoNc.trim() : undefined,
          consumoReal: parseFloat(consumoReal) || 0,
          insumoLoteId: apontamentoOriginal.insumoLoteId ?? undefined,
        },
      });

      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(errJson.message || 'Falha ao processar correção de apontamento.');
      }

      onCorrigido();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao corrigir apontamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CenteredDialog title="Correção Transacional de Apontamento" onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-xl w-full p-6 space-y-5">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{modo === 'ESTORNO_INTEGRAL' ? 'Estornar apontamento para cancelar OP' : 'Correção transacional de apontamento'}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{modo === 'ESTORNO_INTEGRAL' ? 'A correção substituta será registrada integralmente com valores zero.' : 'Estorno integral no ledger e geração de novo evento'}</p>
          </div>
        </div>

        {/* Timeline Read-Only: Apontamento Original */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 text-xs font-mono">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            Apontamento Original (Permanecerá na Trilha de Auditoria)
          </div>
          <div className="grid grid-cols-3 gap-2 pt-1 text-slate-700">
            <div>
              <span className="text-slate-400 block text-[10px]">Qtd Boa:</span>
              <span className="font-bold">{apontamentoOriginal.quantidadeBoa} un</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px]">Qtd NC:</span>
              <span className="font-bold text-red-600">{apontamentoOriginal.quantidadeNaoConforme} un</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px]">Consumo Real:</span>
              <span className="font-bold">{apontamentoOriginal.consumoReal ?? 0}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg font-medium" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          {modo === 'ESTORNO_INTEGRAL' ? <p className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">A quantidade boa, não conforme e o consumo serão corrigidos para zero. Informe a justificativa para registrar o estorno auditável.</p> : <><div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                Qtd Boa Corrigida *
              </label>
              <input
                type="number"
                required
                min="0"
                value={quantidadeBoa}
                onChange={(e) => setQuantidadeBoa(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                Qtd Não Conforme *
              </label>
              <input
                type="number"
                min="0"
                value={quantidadeNaoConforme}
                onChange={(e) => setQuantidadeNaoConforme(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {qtdNcNum > 0 && (
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                Motivo da Não Conformidade *
              </label>
              <input
                type="text"
                required
                value={motivoNc}
                onChange={(e) => setMotivoNc(e.target.value)}
                placeholder="Descreva a não conformidade identificada"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <div>
            <label htmlFor="apontamento-justificativa" className="block text-xs font-bold text-slate-700 uppercase mb-1">
              Consumo Real Corrigido
            </label>
            <input
              type="number"
              step="any"
              min="0"
              value={consumoReal}
              onChange={(e) => setConsumoReal(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-blue-500"
            />
          </div></>}

          <div>
            <label htmlFor="apontamento-justificativa" className="block text-xs font-bold text-slate-700 uppercase mb-1">
              {modo === 'ESTORNO_INTEGRAL' ? 'Justificativa do estorno (obrigatória) *' : 'Justificativa (obrigatória) *'}
            </label>
            <textarea
              id="apontamento-justificativa"
              required
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Justifique formalmente o motivo da retificação..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-[11px] text-amber-800">
            A correção gera um estorno integral por eventos inversos no ledger e um novo apontamento correto. O
            apontamento original permanecerá visível na trilha de auditoria (D-15).
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-100"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !isFormValid}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow transition disabled:opacity-40"
            >
              {loading ? 'Processando...' : modo === 'ESTORNO_INTEGRAL' ? 'Confirmar estorno do apontamento' : 'Confirmar correção'}
            </button>
          </div>
        </form>
      </div>
    </CenteredDialog>
  );
}
